/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 *
 * Provides access to the feature-config resource of the feature-config service.
 *
 * Features:
 * - `load$()` — fetches the feature-flag config for the current user's organisation
 *   via a cached, deduplicated GET request using `ApiClient.getShared()`.
 *   The response is shared across all concurrent subscribers and cached for
 *   5 minutes (default TTL).
 * - `invalidateCache()` — evicts the cached entry so the next `load$()` call
 *   fetches fresh data. Call this when the active organisation changes.
 */

import {inject, Injectable} from '@angular/core';
import {firstValueFrom, Observable, switchMap} from 'rxjs';
import {filter, map} from 'rxjs/operators';

import {environment} from '../environments/environment';
import {AppSettings} from '../models/Constants';
import {FeatureFlag, OrganizationLogoDetails} from '../models/FeatureFlag';
import {ApiClient} from './api-client.service';
import {RequestCacheService} from './request-cache.service';
import {UserService} from './user.service';

/** Cache-key prefix used for the feature-config endpoint. */
const FEATURE_CONFIG_URL_PREFIX = `${environment.backend.feature_config_host}/feature-config`;

/** Normalizes one logo-theme entry, filling missing fields with defaults. */
function normalizeLogoTheme(
  theme: OrganizationLogoDetails | undefined,
): OrganizationLogoDetails {
  return {
    data: theme?.data || AppSettings.THEME_LOGO_DEFAULT,
    type: theme?.type || AppSettings.THEME_LOGO_TYPE_DEFAULT,
    width: theme?.width || AppSettings.THEME_LOGO_WIDTH_DEFAULT,
    height: theme?.height || AppSettings.THEME_LOGO_HEIGHT_DEFAULT,
  };
}

/** Normalizes a raw server response, filling every optional field with its default. */
function normalise(raw: FeatureFlag): FeatureFlag {
  return {
    grantedApps: raw.grantedApps || AppSettings.GRANTED_APPS_DEFAULT,
    organizationTheme: {
      darkTheme:
        raw.organizationTheme?.darkTheme || AppSettings.DARK_THEME_DEFAULT,
      lightTheme:
        raw.organizationTheme?.lightTheme || AppSettings.LIGHT_THEME_DEFAULT,
    },
    features: {
      cx: raw.features?.cx || AppSettings.CX_FEATURE_DEFAULT,
      supplierApi:
        raw.features?.supplierApi || AppSettings.SUPPLIER_API_FEATURE_DEFAULT,
      passport: {
        hierarchy:
          raw.features?.passport?.hierarchy ||
          AppSettings.PASSPORT_HIERARCHY_FEATURE_DEFAULT,
      },
    },
    organizationLogo: {
      lightTheme: normalizeLogoTheme(raw.organizationLogo?.lightTheme),
      darkTheme: normalizeLogoTheme(raw.organizationLogo?.darkTheme),
    },
    organizationText:
      raw.organizationText || AppSettings.ORGANIZATION_TEXT_DEFAULT,
  };
}

@Injectable({
  providedIn: 'root',
})
export class FeatureFlaggingService {
  private readonly apiClient = inject(ApiClient);
  private readonly cache = inject(RequestCacheService);
  private readonly userService = inject(UserService);

  // ─── Observable API (new) ────────────────────────────────────────────────────

  /**
   * Returns the feature-flag configuration for the current user's organization.
   *
   * The underlying GET request is deduplicated and cached for 5 minutes via
   * `ApiClient.getShared()`. Concurrent calls share a single in-flight request
   * and subsequent calls within the TTL window are served from cache.
   *
   * Errors propagate to `GlobalErrorHandlerService` automatically (Pattern A).
   *
   * @returns An `Observable` emitting the normalized `FeatureFlag` object.
   */
  load$(): Observable<FeatureFlag> {
    return this.userService.userDataChange.pipe(
      filter((user) => !!user?.organization?.id),
      map((user) => user?.organization?.id ?? ''),
      switchMap((orgId) =>
        this.apiClient
          .getShared<FeatureFlag>(`${FEATURE_CONFIG_URL_PREFIX}/${orgId}`)
          .pipe(map(normalise)),
      ),
    );
  }

  /**
   * Invalidates all cached feature-config entries.
   *
   * Call this whenever the active organization changes so that the next
   * `load$()` call fetches fresh configuration from the server.
   */
  invalidateCache(): void {
    this.cache.invalidateByPrefix(FEATURE_CONFIG_URL_PREFIX);
  }

  // ─── Deprecated Promise wrappers (backward-compat) ──────────────────────────

  /**
   * @deprecated Use `load$()` instead.
   * Promise-based bridge kept for backward compatibility.
   * Will be removed once all callers have migrated to the Observable API.
   */
  async load(): Promise<FeatureFlag> {
    return firstValueFrom(this.load$());
  }
}
